import type { DownloadItem } from '@atlas-control/shared';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Download, ExternalLink, Lock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { pastRunQuery, pastRunStagesQuery } from '@/features/runs/api';
import { AtlasStatusBadge, StageTimeline } from '@/features/runs/components';
import { api, buildUrl } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { guard } from '@/lib/guards';

export const Route = createFileRoute('/_app/past-runs/$atlasRunId')({
  beforeLoad: ({ context }) => guard(context.me, 'runs.read'),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(pastRunQuery(params.atlasRunId)),
  component: PastRunPage,
});

/** A run Atlas made before the control panel: everything visible, nothing actionable. */
function PastRunPage() {
  const { atlasRunId } = Route.useParams();
  const { data: run } = useSuspenseQuery(pastRunQuery(atlasRunId));
  const stages = useQuery(pastRunStagesQuery(atlasRunId));
  const downloads = useQuery({
    queryKey: ['past-runs', 'downloads', atlasRunId],
    queryFn: () =>
      api<{ items: DownloadItem[] }>(`/past-runs/${atlasRunId}/downloads`).then((r) => r.items),
  });

  const facts: [string, string][] = [
    ['Atlas run', run.atlasRunId],
    ['Started', formatDateTime(run.createdAt)],
    ['Finished', formatDateTime(run.completedAt)],
    ['Stages done', `${run.stagesDone} of ${run.stagesTotal}`],
    ['Board email', run.viewerEmail ?? '-'],
    ['Blueprint', run.hasBlueprint ? 'Yes' : 'No'],
  ];
  if (run.statusReason) facts.push(['Status reason', run.statusReason]);

  return (
    <>
      <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
        <Link to="/runs" search={{ group: 'external' }}>
          <ArrowLeft /> External runs
        </Link>
      </Button>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {run.company || 'Name not recorded'}
        </h1>
        <AtlasStatusBadge status={run.status} />
      </div>
      <Alert className="mb-4">
        <Lock className="size-4" />
        <AlertDescription>
          Started in Atlas, not from the control panel. Read only: no reviews, controls or client
          email from here.
        </AlertDescription>
      </Alert>

      <Card className="mb-6">
        <CardContent>
          {stages.data ? (
            <StageTimeline stages={stages.data} />
          ) : (
            <p className="text-sm text-muted-foreground">
              {stages.isError
                ? 'Stage progress is unavailable (Atlas did not answer).'
                : 'Loading stages…'}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Run</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
              {facts.map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="break-all">{v}</dd>
                </div>
              ))}
            </dl>
            {run.canvasBoardUrl && (
              <Button asChild variant="outline">
                <a href={run.canvasBoardUrl} target="_blank" rel="noreferrer noopener">
                  Open Canvas board <ExternalLink />
                </a>
              </Button>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Downloads</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {downloads.data?.map((i) => (
                <li
                  key={`${i.key}:${i.pillarId ?? ''}`}
                  className="flex items-center justify-between gap-3 py-2 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block truncate">{i.label}</span>
                    {i.reason && <span className="text-xs text-muted-foreground">{i.reason}</span>}
                  </span>
                  {i.allowed && i.available && (
                    <Button asChild variant="outline" size="sm">
                      <a
                        href={buildUrl(`/past-runs/${atlasRunId}/downloads/${i.key}`, {
                          pillar: i.pillarId ?? undefined,
                        })}
                        download
                      >
                        <Download /> Download
                      </a>
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
